import styled, { css } from 'styled-components';
import { remCalc, ratingStarColors } from '@prisjakt/ui-base';

import StarIconsComponent from './star-icons';

import { Size, CounterPosition, CenterAlign } from './types';

const sharedStyleSizeSmall = css`
  font-size: ${remCalc(12)};
  line-height: 1.4;
`;

const sharedStyleSizeLarge = css`
  font-size: ${remCalc(16)};
  line-height: 1.4;
`;

const sharedStylesRateText = css`
  display: inline;
  line-height: 1.2;
`;

interface CounterProps {
  size: Size;
  counterPosition: CounterPosition;
  $linkStyles?: boolean;
}

export const CounterWrapper = styled.div`
  color: ${p => p.theme.colors.base500};
`;

export const Counter = styled.span<CounterProps>(
  props => css`
    margin: 0;
    line-height: 1.2;

    ${props.$linkStyles &&
      css`
        text-decoration: underline;
        &:hover {
          color: ${p => p.theme.text.callToActionHover};
        }
      `}


    ${props.size === 'small' &&
      css`
        ${sharedStyleSizeSmall};
      `}

      ${props.size === 'large' &&
        css`
          ${sharedStyleSizeLarge};
        `}

    ${props.counterPosition === 'right' &&
      css`
        color: ${p => p.theme.text.tertiary};
        position: relative;
      `}

    ${props.counterPosition === 'bottom' &&
      css`
        padding-left: 0;
        display: block;
        width: 100%;
      `}
  `
);

interface RatingProps {
  centerAlign?: CenterAlign;
}

export const Rating = styled.div.attrs({ 'data-test': 'Rating' })<RatingProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${remCalc(2)};

  ${props =>
    props.centerAlign &&
    css`
      justify-content: center;
    `}
`;

interface StarsWrapperProps {
  counterPosition: CounterPosition;
}

export const StarsWrapper = styled.div<StarsWrapperProps>(
  props => css`
    align-items: center;
    gap: ${remCalc(4)};

    ${props.counterPosition === 'right' &&
      css`
        display: flex;
      `}
  `
);

export const StarIcons = styled(StarIconsComponent)`
  display: flex;
`;

interface RateTextProps {
  size: Size;
}

export const RateText = styled.span<RateTextProps>(props => {
  return css`
    font-weight: normal;
    ${sharedStylesRateText};

    p {
      margin-bottom: 0;
    }

    ${props.size === 'small' &&
      css`
        ${sharedStyleSizeSmall};
      `}

    ${props.size === 'large' &&
      css`
        ${sharedStyleSizeLarge};
      `}
  `;
});

interface RateNumberProps {
  size: Size;
}

export const RateNumber = styled.span<RateNumberProps>(
  props => css`
    ${sharedStylesRateText};
    font-weight: bold;

    ${props.size === 'small' &&
      css`
        ${sharedStyleSizeSmall};
      `}

    ${props.size === 'large' &&
      css`
        ${sharedStyleSizeLarge};
      `}
  `
);

export const Fieldset = styled.fieldset`
  float: left;
  padding: 0;
  margin: 0;
  border: none;
  /* Float right to be able to use sibling selector (~) on hover
  to be able to fill all previous icons with correct direction */
  > label {
    float: right;
    cursor: pointer;
  }

  > input {
    display: none;
  }

  /* highlight current and previous icons when checked */
  > input:checked ~ label {
    svg path {
      fill: ${ratingStarColors.active};
    }
  }

  &:not(:checked) > label:hover, /* highlight current icon on hover */
      &:not(:checked) > label:hover ~ label, /* highlight previous icons on hover */
      > input:checked + label:hover, /* highlight current icon on change */
      > input:checked ~ label:hover,
      > input:checked ~ label:hover ~ label {
    svg path {
      fill: ${ratingStarColors.active};
    }
  }
`;
